/**
 * Bronkaartjes groeperen per document.
 *
 * Citaties komen per chunk binnen, dus één document leverde net zoveel
 * bronkaartjes op als er fragmenten uit gehaald waren (melding behandelaar
 * 2026-08: drie kaartjes "Protocol cannabis jongeren" onder één antwoord).
 *
 * Staat los van de pagina zodat de groepeerlogica zonder React getest kan
 * worden; de vitest-omgeving van dit project is `node`, zonder jsdom.
 */

/** De velden die het bronkaartje toont en die dus de groepering bepalen. */
export interface GroupableCitation {
  title: string;
  sourceFile?: string;
  author?: string;
  ring?: string;
  ring2Source?: string;
  ring3Source?: string;
  sourceUrl?: string;
}

export interface CitationGroup<T extends GroupableCitation> {
  key: string;
  /** Chunks in de volgorde waarin het antwoord ze aanhaalt; [0] is de sterkste. */
  citations: T[];
  /** 1-based citatienummers zoals ze als [n] in de prose staan. */
  indices: number[];
}

/**
 * De groepeersleutel is het bronbestand plus álle metadata die het kaartje
 * toont: ring, bronlabel, doel-URL, titel en auteur. Chunks die daarin
 * verschillen krijgen dus een eigen kaartje, zodat samenvoegen nooit een chip
 * of auteur laat verdwijnen. De volgorde volgt het eerste voorkomen, waardoor
 * de sterkst scorende bron bovenaan blijft staan.
 *
 * `sourceFile` kan een lege string zijn wanneer het chunk-manifest het veld
 * mist, daarom valt de sleutel dan terug op de titel in plaats van alle
 * veldloze citaties op één hoop te gooien.
 */
export function groupCitations<T extends GroupableCitation>(citations: T[]): CitationGroup<T>[] {
  const groups: CitationGroup<T>[] = [];
  const byKey = new Map<string, CitationGroup<T>>();
  citations.forEach((c, i) => {
    const key = [
      c.sourceFile?.trim() || c.title,
      c.ring ?? 'ring1',
      c.ring2Source ?? '',
      c.ring3Source ?? '',
      c.sourceUrl ?? '',
      c.author ?? '',
      c.title,
    ].join(' | ');
    const existing = byKey.get(key);
    if (existing) {
      existing.citations.push(c);
      existing.indices.push(i + 1);
      return;
    }
    const group: CitationGroup<T> = { key, citations: [c], indices: [i + 1] };
    byKey.set(key, group);
    groups.push(group);
  });
  return groups;
}

/**
 * Telling voor de kop boven de bronkaartjes.
 *
 * De kaartjes zijn sinds de groepering per document, maar de [n]-nummers in de
 * lopende tekst lopen nog per fragment. Eén getal in de kop sprak die nummers
 * dus tegen: "Bronnen (1)" boven een kaartje met de nummers 1, 2 en 3. Zodra de
 * twee tellingen verschillen noemt de kop ze allebei, zodat wie "bron 3" zoekt
 * niet leest dat er maar één is.
 */
export function citationCountLabel(documents: number, fragments: number): string {
  if (documents === fragments) return String(documents);
  const doc = documents === 1 ? 'document' : 'documenten';
  const frag = fragments === 1 ? 'fragment' : 'fragmenten';
  return `${documents} ${doc}, ${fragments} ${frag}`;
}
